import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { useState } from "react";

import { NumberedCard } from "@/components/editorial/NumberedCard";
import { SectionHead } from "@/components/editorial/SectionHead";
import { listDocuments } from "@/services/api/documents";

const PAGE_SIZE = 24;

export function Library() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const offset = page * PAGE_SIZE;

  const { data, isLoading, error } = useQuery({
    queryKey: ["documents", page, search],
    queryFn: () =>
      listDocuments({
        limit: PAGE_SIZE,
        offset,
        search: search.trim() || undefined,
      }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="space-y-10">
      <SectionHead label="Library" italicSuffix="regulatory documents" count={total || undefined} />

      <div className="flex gap-2">
        <input
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
          placeholder="Search regulation code or title…"
          className="composer max-w-md"
        />
      </div>

      {isLoading ? (
        <p className="text-warmgray-400">Loading…</p>
      ) : error ? (
        <p className="text-oxblood-500">Could not load documents: {(error as Error).message}</p>
      ) : items.length === 0 ? (
        <p className="font-serif italic text-warmgray-400">No papers match this search.</p>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {items.map((doc, idx) => (
              <NumberedCard
                key={doc.documentId}
                index={offset + idx + 1}
                tag={doc.regulationCode ?? "regulation"}
                heading={doc.title}
                dek={
                  doc.publicationDate
                    ? format(parseISO(doc.publicationDate), "MMMM yyyy")
                    : undefined
                }
                meta={
                  <span>
                    {doc.sectionCount} sections{doc.jurisdiction ? ` · ${doc.jurisdiction}` : ""}
                  </span>
                }
              />
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-ruleline pt-4 text-xs text-warmgray-400">
            <span className="font-mono">
              Page {page + 1} of {lastPage + 1}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="pill disabled:opacity-40"
              >
                ← Previous
              </button>
              <button
                type="button"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                className="pill disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
